import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subject, takeUntil, tap } from 'rxjs';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import BlockModel from '../models/email-template/block-model.interface';
import { TemporaryMedia } from '../models/email-template/temporary-media.interface';
import { TemplateService } from './template.service';
import { GlobalTemplateService } from './global-template.service';
import { VariableData } from '../models/other/variable-data';

@Injectable({ providedIn: 'root' })
export class TemplateExportService {

  /** Same inclusion rule `generateContentString` renders from - a variable is
   *  exportable unless it's a link/href/image URL (alt text is the exception:
   *  it's still exported even though its name contains "image"). Shared so
   *  the field count shown in the export dialog (AC1, RCA-404) always matches
   *  what actually gets exported. */
  private getExportableFields(selectedBlocks: BlockModel[], variablesMaxLength: any[]): { variable: string; value: string; maxLength: any }[] {
    const fields = [];
    for (const block of selectedBlocks) {
      const variables = block.variables;
      for (const variable in variables) {
        const variableLowerCase = variable.toLowerCase();
        const isLinkOrImage = variableLowerCase.includes('link') ||
          variableLowerCase.includes('href') ||
          variableLowerCase.includes('img') ||
          variableLowerCase.includes('image');
        const isAlt = variableLowerCase.includes('alt');

        if (!isLinkOrImage || isAlt) {
          const foundVariable: VariableData = variablesMaxLength.find(
            (wantedVariable) => wantedVariable.name === variable
          );
          fields.push({ variable, value: variables![variable] as string, maxLength: foundVariable?.maxLength });
        }
      }
    }
    return fields;
  }

  generateContentString(selectedBlocks: BlockModel[], variablesMaxLength: any[]) {
    const fields = this.getExportableFields(selectedBlocks, variablesMaxLength);
    const stringBuilder = fields.map(
      (field) =>
        `<div class="output-text" style="color:red !important;"><strong>${field.variable}<br>Maximum length ${field.maxLength || 'Undefined'}:</strong></div>${field.value}<br>`
    );
    // join the array into a string
    let finalString = stringBuilder.join('<br/>');
    return finalString;
  }

  /** Number of fields the export will actually include - shown in the export
   *  dialog's summary before the user picks download/copy (AC1, RCA-404). */
  getExportFieldCount(selectedBlocks: BlockModel[], variablesMaxLength: any[]): number {
    return this.getExportableFields(selectedBlocks, variablesMaxLength).length;
  }

  downloadOutput(contentString: string, templateName: string): void {
    const paragraphs = this.buildDocxParagraphs(contentString);

    const doc = new Document({
      sections: [{ properties: {}, children: paragraphs }],
    });

    Packer.toBlob(doc).then((blob) => {
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `${templateName}.docx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    });
  }

  private buildDocxParagraphs(contentString: string): Paragraph[] {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = contentString;

    const paragraphs: Paragraph[] = [];
    let valueNodes: Node[] = [];
    let header: { variable: string; maxLength: string } | null = null;

    const flush = () => {
      if (!header) return;
      paragraphs.push(new Paragraph({
        spacing: { after: 120 },
        children: [
          new TextRun({ text: header.variable, bold: true, color: 'FF0000' }),
          new TextRun({ text: `Maximum length ${header.maxLength}:`, bold: true, color: 'FF0000', break: 1 }),
        ],
      }));
      paragraphs.push(...this.buildValueParagraphs(valueNodes));
      paragraphs.push(new Paragraph({ children: [new TextRun({ break: 1 })] }));
      valueNodes = [];
      header = null;
    };

    for (const node of Array.from(wrapper.childNodes)) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).matches('div.output-text')) {
        flush();
        header = this.parseHeader(node as Element);
      } else {
        valueNodes.push(node);
      }
    }
    flush();

    return paragraphs;
  }

  private parseHeader(div: Element): { variable: string; maxLength: string } {
    const strong = div.querySelector('strong');
    const headerHtml = strong?.innerHTML || '';
    const [variablePart, maxLine] = headerHtml.split(/<br\s*\/?>/i);
    const maxMatch = maxLine?.match(/Maximum length (.+?):/);
    return {
      variable: (variablePart || '').trim(),
      maxLength: maxMatch?.[1] || 'Undefined',
    };
  }

  private buildValueParagraphs(nodes: Node[]): Paragraph[] {
    const trimmed = this.trimTrailingBreaks(nodes);

    const paragraphs: Paragraph[] = [];
    let runs: TextRun[] = [];
    let runText = '';
    let listLevel = -1;

    const closePara = (): boolean => {
      if (runText.length === 0) return false;
      paragraphs.push(new Paragraph({
        children: runs,
        ...(listLevel >= 0 ? { bullet: { level: listLevel } } : {}),
      }));
      runs = [];
      runText = '';
      return true;
    };

    const handleBr = () => {
      if (!closePara()) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ break: 1 })] }));
      }
    };

    const walk = (node: Node, fmt: { bold: boolean; italics: boolean; underline: boolean }) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text.length === 0) return;
        runs.push(new TextRun({
          text,
          bold: fmt.bold || undefined,
          italics: fmt.italics || undefined,
          underline: fmt.underline ? {} : undefined,
        }));
        runText += text;
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const el = node as Element;
      const tag = el.tagName.toLowerCase();

      if (tag === 'br') {
        handleBr();
        return;
      }
      if (tag === 'p' || tag === 'div') {
        closePara();
        el.childNodes.forEach((c) => walk(c, fmt));
        closePara();
        return;
      }
      if (tag === 'ul' || tag === 'ol') {
        closePara();
        listLevel++;
        el.childNodes.forEach((c) => walk(c, fmt));
        listLevel--;
        closePara();
        return;
      }
      if (tag === 'li') {
        closePara();
        el.childNodes.forEach((c) => walk(c, fmt));
        closePara();
        return;
      }

      const childFmt = {
        bold: fmt.bold || tag === 'b' || tag === 'strong',
        italics: fmt.italics || tag === 'i' || tag === 'em',
        underline: fmt.underline || tag === 'u',
      };
      el.childNodes.forEach((c) => walk(c, childFmt));
    };

    trimmed.forEach((n) => walk(n, { bold: false, italics: false, underline: false }));
    closePara();

    return paragraphs;
  }

  private trimTrailingBreaks(nodes: Node[]): Node[] {
    let end = nodes.length;
    while (end > 0) {
      const last = nodes[end - 1];
      if (last.nodeType === Node.ELEMENT_NODE && (last as Element).tagName.toLowerCase() === 'br') {
        end--;
        continue;
      }
      if (last.nodeType === Node.TEXT_NODE && (last.textContent || '').trim() === '') {
        end--;
        continue;
      }
      break;
    }
    return nodes.slice(0, end);
  }

  /** Copies as rich text (HTML + plain-text fallback in the same clipboard
   *  write) so pasting into Word keeps the bold red field-name headers - AC3,
   *  RCA-404. `copyToClipboard` above only ever wrote plain text. Falls back
   *  to plain text if the browser lacks `ClipboardItem`/`clipboard.write`. */
  async copyRichToClipboard(contentString: string): Promise<void> {
    const plain = this.extractTextFromDiv(this.removeHtmlTags(contentString));

    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      const html = `<!doctype html><html><head><meta charset="utf-8"></head><body>${contentString}</body></html>`;
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      return;
    }

    await navigator.clipboard.writeText(plain);
  }

  copyToClipboard(contentString: string): void {
    contentString = this.removeHtmlTags(contentString);

    contentString = this.extractTextFromDiv(contentString);

    // copy the final string to the clipboard
    navigator.clipboard.writeText(contentString);
  }

  private extractTextFromDiv(contentString: string) {
    let tempDiv = document.createElement("div");
    tempDiv.innerHTML = contentString;
    return tempDiv.textContent || tempDiv.innerText;

  }

  private removeHtmlTags(tempString: string): string {
    tempString = tempString.replace(/<br\/>/g, '\n'); // Removes <br/> tags and replaces them with newlines
    tempString = tempString.replace(/<strong\>/g, '');
    tempString = tempString.replace(/<\/strong\>/g, '');

    return tempString;
  }

}
